import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { TextFilter } from "../../components/common/TextFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { LANGUAGE_KEY_IN_LOCAL_STORAGE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import useDialogWithId from "../../hooks/useDialogWithId";
import useLocalStorage from "../../hooks/useLocalStorage";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useCustomerStatuses, useCustomersFullData } from "../../services/customersService";
import TableService from "../../services/tableService";
import { CustomerAddCompanyDialog } from "./CustomerAddCompanyDialog";
import { CustomerAddPersonDialog } from "./CustomerAddPersonDialog";
import { CUSTOMERS_TABLE_NAME, useCustomersTableFieldsConfig } from "./CustomerPageTableConfig";
import { ClientPassportDialog } from "./Passport/ClientPassportDialog";

export function CustomerPage(props) {
    const CUSTOMERS_TABLE_FIELDS_CONFIG = useCustomersTableFieldsConfig();

    const TABLE_CONFIGS = [{ name: CUSTOMERS_TABLE_NAME, config: CUSTOMERS_TABLE_FIELDS_CONFIG }];

    const { pageName } = props;
    // const dialogs = useDialogs

    const { t } = useTranslation();
    // check permission if is required see useCheckPermission hook
    // const [hasEditPermission] = useCheckPermission(EDIT_PERMISSION)
    const [hasEditPermission] = [true];

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    // State for client passport dialog
    const [openClientPassportDialog, setOpenClientPassportDialog] = useState(false);
    const [selectedClientId, setSelectedClientId] = useState(undefined);

    // Dialog for adding a person customer
    const [openAddPersonDialog, _addPersonDialogId, onOpenAddPersonDialog, onCloseAddPersonDialog] =
        useDialogWithId();

    const { openDialog: openAddCompany } = useDialogBetter();
    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const customerDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            CUSTOMERS_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME],
        );
    }, [
        // @ts-ignore
        hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME],
        // @ts-ignore
        columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME],
        CUSTOMERS_TABLE_FIELDS_CONFIG,
    ]);

    const { data: customersRows, isLoading, refetch } = useCustomersFullData(searchParams);

    useEffect(() => {
        if (customersRows?.results) {
            setDataLocal(customersRows.results);
            setRecordsCount(customersRows.count);
        }
    }, [customersRows, dataPage, dataPageSize]);

    // @ts-ignore
    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
        // This function can be used for row buttons if needed
    }, []);

    const closeAddPersonDialog = useCallback(() => {
        setSelectedRowId(undefined);
        setCheckedAll(false);
        // @ts-ignore
        onCloseAddPersonDialog();
        refetch();
    }, [onCloseAddPersonDialog, refetch]);

    const closeClientPassportDialog = useCallback(() => {
        setSelectedClientId(undefined);
        setSelectedRowId(undefined);
        setOpenClientPassportDialog(false);
        refetch();
    }, [refetch]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                CUSTOMERS_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            CUSTOMERS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const [language, _] = useLocalStorage(LANGUAGE_KEY_IN_LOCAL_STORAGE, "pl");

    const { data: customerStatusesData } = useCustomerStatuses();

    const getFilteringData = useCallback(() => {
        return {
            statuses: customerStatusesData,
            language: language,
        };
    }, [customerStatusesData, language]);

    useEffect(() => {
        refetch();
        console.log("params changed to", searchParams.toString());
    }, [searchParams]);

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.clients")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "start",
                            width: "40%",
                            marginLeft: "50px",
                        }}
                    >
                        <TextFilter
                            filterKey={"master"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            resetPageNumber={resetPageNumber}
                            withPagination
                            placeholder={t("clients.filter.masterFilterPlaceholder") ?? undefined}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        filteringData={getFilteringData()}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"customerPerson"}
                        style={{ marginBlock: "5px" }}
                        // We have separate buttons for creating company and person
                        showCreateButton={false}
                        mdGridButton={hasEditPermission ? 1.25 : 2.75}
                        showImportList={false}
                        showRemoveLeadButton={false}
                        showAddCustomerPersonButton={true}
                        showAddCustomerCompanyButton={true}
                        // @ts-ignore
                        onClickAddCustomerPerson={() => onOpenAddPersonDialog()}
                        // @ts-ignore
                        onClickAddCustomerCompany={() => {
                            openAddCompany({
                                Component: CustomerAddCompanyDialog,
                                componentProps: {},
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    CUSTOMERS_TABLE_FIELDS_CONFIG, // @ts-ignore
                                    hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME]
                                        ? // @ts-ignore
                                          hiddenColumnsForTables?.[CUSTOMERS_TABLE_NAME]
                                        : [],
                                ),
                                // @ts-ignore
                                columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[CUSTOMERS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={"customers/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={customerDataExportHeaders}
                            exportToFileFileName={`${t("customer.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={isLoading}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
            {openAddPersonDialog ? (
                <CustomerAddPersonDialog
                    open={openAddPersonDialog}
                    onClose={closeAddPersonDialog}
                    openAddLeadDialogAfterSubmit={true}
                />
            ) : (
                <></>
            )}
            {openClientPassportDialog && selectedClientId ? (
                <ClientPassportDialog
                    open={openClientPassportDialog}
                    onClose={closeClientPassportDialog}
                    clientId={selectedClientId}
                />
            ) : (
                <></>
            )}
        </DefaultPageWrapper>
    );
}
